import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { IInvoiceSearch, IPatient } from '@patient-ui/shared/models';
import { AnyObject } from '@patient-ui/shared/utils';

import { isPatientPortalHost } from './pendo-host.util';
import { PendoState } from './pendo.reducer';
import { selectPendoInitializeSuccess } from './pendo.selectors';
import { WindowRef } from './window-ref.services';

/** Matches prior `moment().format('YYYY/MM/DD HH:mm:ss')` visitor timestamps. */
function formatPendoTimestamp(date = new Date()): string {
  const pad = (value: number) => value.toString().padStart(2, '0');
  return (
    `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(
      date.getDate()
    )} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`
  );
}

/** How the user located labs at the point of search, set in application code. */
export type SearchMethod = 'locate_me' | 'zip_code' | 'address';

/**
 * Search inputs captured on the Find a Lab Results page. Only the fields this
 * app actually exposes are included; `search_method` is derived in application
 * code at the point of search. Visitor context (region, browser, OS, device,
 * referrer, etc.) is auto-captured by Pendo and not passed explicitly.
 */
export interface FindALabSearchEvent {
  search_method: SearchMethod;
  preferred_date: string;
  is_same_day_search: boolean;
  days_until_preferred_date: number;
  address_single?: string;
  zip?: string;
  lat?: number;
  lon?: number;
  service?: number;
  radius?: number;
  time_of_day?: string;
  open_now?: boolean;
  open_weekends?: boolean;
  labcorp_at_walgreens?: boolean;
}

/** Fields built from form/URL inputs before date metrics are applied. */
export type FindALabSearchEventCore = Omit<
  FindALabSearchEvent,
  'preferred_date' | 'is_same_day_search' | 'days_until_preferred_date'
>;

/**
 * Standard (non-reschedule) slot hold on Find a Lab Results.
 */
export interface FindALabSlotSelectedEvent {
  locationId: number;
  slot: string;
  service: number;
  preferred_date: string;
  search_method: SearchMethod;
  is_same_day_search: boolean;
  days_until_preferred_date: number;
  days_from_preferred_to_slot: number;
  zip?: string;
  lat?: number;
  lon?: number;
}

/**
 * Standard (non-reschedule) appointment completion on Review.
 */
export interface AppointmentScheduledEvent {
  locationId: number;
  booked_slot: string;
  service: number;
  preferred_date?: string;
  zip?: string;
  paidBy: string;
  days_out_from_today: number;
  days_search_to_booking?: number;
  search_context_missing?: boolean;
}

export type OrbiAgentEntryState = 'new' | 'active';

export type OrbiAgentInputMethod = 'typed' | 'voice' | 'suggested_prompt';

export type OrbiAgentResponseType =
  | 'text'
  | 'results_card'
  | 'appointment_card'
  | 'find_a_lab_card'
  | 'patient_pass_card'
  | 'rate_visit_card'
  | 'find_a_bill_card'
  | 'billing_redirect'
  | 'appointment_schedule'
  | 'guardrail'
  | 'error';

export type OrbiAgentBillingNavigation =
  | 'pay_a_bill'
  | 'payment_plan'
  | 'update_insurance'
  | 'view_invoices'
  | 'invoice_history';

export type OrbiAgentCardType =
  | 'appointment'
  | 'find_a_lab'
  | 'patient_pass'
  | 'find_a_bill'
  | 'results'
  | 'rate_visit';

export type OrbiAgentCardAction =
  | 'mobile_check_in'
  | 'appointment_details'
  | 'provide_insurance'
  | 'review_insurance'
  | 'update_insurance'
  | 'see_all'
  | 'find_lab_location'
  | 'add_to_wallet'
  | 'pay_my_bill'
  | 'view_result'
  | 'view_all_results'
  | 'rate_visit';

export type OrbiAgentCardVariant = 'upcoming' | 'within_30_min' | 'checked_in';

export type OrbiAgentInsuranceState = 'provide' | 'review' | 'update';

export type OrbiAgentWalletType = 'apple' | 'google';

export interface OrbiAgentOpenedEvent {
  source_page: string;
  entry_state: OrbiAgentEntryState;
}

export interface OrbiAgentMessageSentEvent {
  message_index: number;
  input_method: OrbiAgentInputMethod;
  session_id: string;
}

export interface OrbiAgentResponseRenderedEvent {
  response_type: OrbiAgentResponseType;
  agent: string;
  session_id: string;
  billing_navigation?: OrbiAgentBillingNavigation;
  service_type?: string;
  guardrail_type?: string;
  guardrail_locking?: boolean;
}

export interface OrbiAgentCardActionEvent {
  card_type: OrbiAgentCardType;
  action: OrbiAgentCardAction;
  session_id: string;
  card_variant?: OrbiAgentCardVariant;
  insurance_state?: OrbiAgentInsuranceState;
  wallet_type?: OrbiAgentWalletType;
  service?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PendoService {
  constructor(
    public windowRef: WindowRef,
    private readonly store: Store<PendoState>
  ) {}

  getPendoState(): boolean {
    let isInitializeSuccess = false;
    this.store
      .select(selectPendoInitializeSuccess)
      .pipe(take(1))
      .subscribe((state) => {
        isInitializeSuccess = state;
        return isInitializeSuccess;
      });
    return isInitializeSuccess;
  }

  pendoReady(): boolean {
    return (
      this.getPendoState() &&
      typeof this.windowRef.nativeWindow.pendo.isReady === 'function' &&
      this.windowRef.nativeWindow.pendo.isReady()
    );
  }

  pendoInit(visitorInfo: AnyObject): void {
    if (this.pendoReady()) {
      this.windowRef.nativeWindow.pendo.identify(visitorInfo);
    } else {
      this.windowRef.nativeWindow.pendo.initialize(visitorInfo);
      this.store.select(selectPendoInitializeSuccess);
    }
  }

  setUnknownUser(): void {
    this.pendoInit({
      visitor: {
        Role: 'UnknownUser',
        Timestamp: formatPendoTimestamp(),
      },
    });
  }

  setGuestUser(invoiceSearchValues: IInvoiceSearch[]): void {
    const invoiceArray = [...new Set(invoiceSearchValues)];
    let invoicesStr = '';
    invoiceArray.forEach((search, index) => {
      invoicesStr += `${
        search.invoiceNumber
          ? `Invoice number ${search.invoiceNumber}`
          : `Specimen number ${search.specimenNumber}`
      }_${
        search.zipCode.length > 5
          ? search.zipCode.substring(0, 5)
          : search.zipCode
      }`;
      if (invoiceArray.length > index + 1) {
        invoicesStr += ',';
      }
    });
    this.pendoInit({
      visitor: {
        Timestamp: formatPendoTimestamp(),
        InvoiceZip: invoicesStr,
      },
    });
  }

  setRegisteredUser(patient: IPatient) {
    this.pendoInit({
      visitor: {
        id: patient.id?.toString(),
        LPID: patient.hashId,
        Timestamp: formatPendoTimestamp(),
        Gender: patient.gender,
        Dob: patient.dateOfBirth.toString(),
        City: this.getPatientAddressField(patient, 'city'),
        State: this.getPatientAddressField(patient, 'state'),
        Zip: this.getPatientAddressField(patient, 'zipCode'),
      },
    });
  }

  private getPatientAddressField(
    patient: IPatient,
    field: 'city' | 'state' | 'zipCode'
  ): string {
    if (patient.primaryAddress) {
      return patient.primaryAddress[field] ?? '';
    }
    if (patient.addresses?.[0]) {
      return patient.addresses[0][field] ?? '';
    }
    return '';
  }

  /**
   * Records a Find a Lab search when the Results page loads with a valid search.
   */
  trackFindALabSearch(event: FindALabSearchEvent): void {
    this.track('Find A Lab Search', { ...event });
  }

  /** Records a slot hold on Find a Lab Results during a standard booking. */
  trackFindALabSlotSelected(event: FindALabSlotSelectedEvent): void {
    this.track('Find A Lab Slot Selected', { ...event });
  }

  /** Records a completed standard appointment on the Review step. */
  trackAppointmentScheduled(event: AppointmentScheduledEvent): void {
    this.track('Appointment Scheduled', { ...event });
  }

  /**
   * Shared envelope for Track Events. Stamps `product` and a client
   * `timestamp`; Pendo additionally auto-captures visitor region, browser,
   * OS, and device, so those need not be passed explicitly.
   *
   * Guards against missing Pendo, non-Patient Portal hosts, and track
   * failures so analytics never breaks booking flows.
   */
  private track(event: string, props: Record<string, unknown>): void {
    try {
      if (!this.isPatientPortalHost()) {
        return;
      }

      const pendo = this.windowRef.nativeWindow?.pendo;
      if (typeof pendo?.track !== 'function') {
        return;
      }

      pendo.track(event, {
        product: 'Appointments',
        registered_user: true,
        timestamp: new Date().toISOString(),
        ...props,
      });
    } catch {
      // Analytics must never break booking flows.
    }
  }

  /**
   * Patient Portal hosts only (e.g. patient.labcorp.com, patient.dev.labcorp.com,
   * patient-local.labcorp.com). Marketing labcorp.com must not be tracked.
   */
  private isPatientPortalHost(): boolean {
    const hostname = this.windowRef.nativeWindow?.location?.hostname ?? '';
    if (hostname === 'patient-local.labcorp.com') {
      return true;
    }
    return /^patient(\.[a-z0-9-]+)*\.labcorp\.com$/i.test(hostname);
  }

  trackAgentOpened(event: OrbiAgentOpenedEvent): void {
    this.trackAgentEvent('agent_opened', { ...event });
  }

  trackAgentMessageSent(event: OrbiAgentMessageSentEvent): void {
    this.trackAgentEvent('agent_message_sent', { ...event });
  }

  trackAgentResponseRendered(event: OrbiAgentResponseRenderedEvent): void {
    this.trackAgentEvent('agent_response_rendered', { ...event });
  }

  trackAgentCardAction(event: OrbiAgentCardActionEvent): void {
    this.trackAgentEvent('agent_card_action', { ...event });
  }

  private trackAgentEvent(event: string, props: Record<string, unknown>): void {
    try {
      if (
        !isPatientPortalHost(this.windowRef.nativeWindow?.location?.hostname)
      ) {
        return;
      }

      const pendo = this.windowRef.nativeWindow?.pendo;
      if (typeof pendo?.track !== 'function') {
        return;
      }

      pendo.track(event, { ...props, timestamp: new Date().toISOString() });
    } catch {
      return;
    }
  }
}
